// Auth0 browser login for the static origin.
//
// Authorization Code with PKCE, which is the only safe flow for a bundle a
// visitor can read: the client holds no secret, and the code exchange is bound
// to a verifier this page generated.
//
// The access token is held in MEMORY ONLY. auth0-spa-js defaults to that, and it
// is set explicitly below so a later default change cannot quietly move tokens
// into localStorage, where any script injected into this page could read one.
// The cost is that a full page reload has no in-memory token. Auth0 may renew
// the session silently when its configured browser session is available; a
// normal sign-in redirect remains the fallback.
//
// Nothing here is a secret. The domain, client id and audience are public
// identifiers, injected at build time by build.mjs.
//
// The host is the enforcement point, not this module: this bundle is readable by
// anyone who requests it, so nothing it decides is a control. A deployed host
// verifies the token on every route the bundle calls and answers 401 without
// one. If the bundle is served without Auth0 configuration — the local preview,
// which has no tenant to authenticate against — it makes unauthenticated
// requests and the host, in that environment alone, answers them. It does not
// invent a session either way.

import { createAuth0Client } from "@auth0/auth0-spa-js";
import { clearNotificationPromptSession } from "./notification_prompt_session.js";

// Replaced at build time by build.mjs. Empty when the build was given no
// Auth0 configuration.
const CONFIG = __VD_AUTH0__;

// Auth0 compares redirect URIs EXACTLY, trailing slash included. This must stay
// in step with `callback_urls` in infra/stacks/auth0/main.tf.
const REDIRECT_URI = `${window.location.origin}/`;

let client = null;

// A staff access link is an API key in the URL fragment (#k=ssk1.…). The
// fragment never reaches the server, so it is not in any access log; we read
// it once, hold it in memory only, and clear it from the visible address bar.
// The URL the staff member was sent remains their durable copy.
let linkToken = null;
const LINK_TOKEN = /^ssk1\.[A-Za-z0-9_-]{12,64}\.[A-Za-z0-9_-]{43}$/;

/**
 * Take the staff access link out of the fragment, or return null.
 *
 * Parsed exactly as `captureInvitation` parses its own fragment, so the two
 * readers of this one URL cannot drift apart: `URLSearchParams` already
 * percent-decodes, and a fragment carrying anything besides the single `k` is
 * not a link we were sent. A `k` that is present but malformed is cleared all
 * the same — a near-token must not be left sitting in the address bar or in
 * the history entry — and then refused.
 */
function readLinkFromFragment() {
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const values = fragment.getAll("k");
  if (values.length === 0) {
    return null;
  }
  window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
  if (values.length !== 1 || [...fragment.keys()].length !== 1 || !LINK_TOKEN.test(values[0])) {
    return null;
  }
  return values[0];
}

/** Whether this page is running on a staff access link rather than a sign-in. */
export function isLinkSession() {
  return linkToken !== null;
}

/** Forget the link. There is no provider session to end, so this is all of it. */
export function clearLinkSession() {
  linkToken = null;
}

const VALID_TABS = new Set(["home", "stats", "stable", "live", "inspect", "tv", "admin", "inglis-admin"]);

function safeRealm(value) {
  // Realm keys are inert query/header data.  The server validates membership;
  // the browser must not silently turn an unknown or oddly-shaped selection
  // into an implicit default realm during an Auth0 round-trip.
  return typeof value === "string" && value.length > 0 ? value : null;
}

function safeTab(value) {
  return typeof value === "string" && VALID_TABS.has(value) ? value : null;
}

/** Return only the app-owned selectors that are safe to carry through Auth0. */
export function appSelectorsFromLocation(location = window.location) {
  const params = new URL(location.href, window.location.origin).searchParams;
  const selectors = {};
  const realm = safeRealm(params.get("realm"));
  const tab = safeTab(params.get("tab"));
  if (realm) selectors.realm = realm;
  if (tab) selectors.tab = tab;
  return selectors;
}

/** Normalize callback state without accepting a URL, path, or redirect target. */
export function safeAppSelectors(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const selectors = {};
  const realm = safeRealm(value.realm);
  const tab = safeTab(value.tab);
  if (realm) selectors.realm = realm;
  if (tab) selectors.tab = tab;
  return selectors;
}

function selectorPath(selectors) {
  const params = new URLSearchParams();
  if (selectors.realm) params.set("realm", selectors.realm);
  if (selectors.tab) params.set("tab", selectors.tab);
  const query = params.toString();
  return query ? `/?${query}` : "/";
}

/** Whether this build was given an Auth0 tenant to talk to. */
export function isConfigured() {
  return Boolean(CONFIG && CONFIG.domain && CONFIG.clientId && CONFIG.audience);
}

/**
 * Complete any redirect in progress and return the session state.
 *
 * Returns `{ authenticated, configured }`. It never throws for the ordinary
 * "not signed in yet" case: that is a state to render, not an error.
 */
export async function initAuth() {
  // Read before anything else. A link carries its own credential, so the Auth0
  // client is never constructed: there is no tenant account behind a link, and
  // a silent authentication attempt would only cost a round trip and could
  // redirect the holder away from the page their link addressed.
  linkToken = readLinkFromFragment();
  if (linkToken) {
    return { configured: true, authenticated: true, link: true };
  }

  if (!isConfigured()) {
    return { configured: false, authenticated: false };
  }

  client = await createAuth0Client({
    domain: CONFIG.domain,
    clientId: CONFIG.clientId,
    authorizationParams: {
      audience: CONFIG.audience,
      redirect_uri: REDIRECT_URI,
      // Asked for explicitly rather than inherited from the SDK default. The
      // host reads the verified email from /userinfo to match an operator's
      // provider verification status. The host resolves access through the
      // plugin and never creates a membership from this browser profile.
      scope: "openid profile email",
    },
    // Without the audience above Auth0 issues an opaque token rather than a
    // JWT, and every API route 401s while login appears to succeed. The
    // resource server also has to allow offline access for this to work.
    useRefreshTokens: true,
    // A reload (including a realm switch) clears the in-memory refresh token.
    // Let the SDK restore it from an existing Auth0 session using silent PKCE
    // authentication. An expired session still requires an explicit sign-in.
    useRefreshTokensFallback: true,
    cacheLocation: "memory",
  });

  // Returning from the identity provider. Auth0 puts `code` and `state` on the
  // query string; handleRedirectCallback exchanges them.
  const query = new URLSearchParams(window.location.search);
  if (query.has("code") && query.has("state")) {
    const callback = await client.handleRedirectCallback();
    // Drop code/state and restore only the two selectors owned by this app. No
    // callback value is treated as a URL or open redirect target.
    const fromCallback = safeAppSelectors(callback && callback.appState && callback.appState.vd);
    const fromUrl = appSelectorsFromLocation(window.location);
    window.history.replaceState({}, "", selectorPath({ ...fromUrl, ...fromCallback }));
  }

  return { configured: true, authenticated: await client.isAuthenticated() };
}

/** Send the browser to Auth0 to sign in. */
export async function login() {
  // A link holder has no tenant account to sign in with. Sending them to Auth0
  // would strand them at a login screen they cannot complete, so this refuses
  // rather than starting a redirect that cannot end here.
  if (isLinkSession()) {
    return false;
  }
  if (!client) {
    throw new Error("auth is not configured in this build");
  }
  await client.loginWithRedirect({ appState: { vd: appSelectorsFromLocation() } });
}

/** Sign out locally and at the tenant, returning to this origin. */
export async function logout() {
  clearNotificationPromptSession();
  // Ending a link session is simply forgetting the key: there is no provider
  // session to close, and the link the staff member holds is unaffected.
  if (isLinkSession()) {
    clearLinkSession();
    window.location.assign("/");
    return;
  }
  if (!client) {
    return;
  }
  await client.logout({ logoutParams: { returnTo: REDIRECT_URI } });
}

/**
 * The current access token, or null when there is no session.
 *
 * Silently renews through the rotating refresh token when the access token has
 * expired. A failure here is a genuine "no longer signed in", so it resolves to
 * null rather than throwing into a render.
 */
export async function accessToken() {
  // The link's own key, held in memory only. It is never renewed: the host
  // decides how long it lives and answers 401 the moment it stops being valid.
  if (linkToken) {
    return linkToken;
  }
  if (!client) {
    return null;
  }
  try {
    return await client.getTokenSilently();
  } catch (error) {
    if (error && (error.error === "login_required" || error.error === "consent_required")) {
      return null;
    }
    console.error("token renewal failed", error);
    return null;
  }
}

/** The signed-in user's profile, for display only. Never authority. */
export async function profile() {
  if (!client) {
    return null;
  }
  return (await client.getUser()) || null;
}
